export const COGNITO_GROUPS = {
  USERS: 'users',
  LAWYERS: 'lawyers',
  ADMINS: 'admins',
} as const;

export type CognitoGroup = (typeof COGNITO_GROUPS)[keyof typeof COGNITO_GROUPS];

export const TASK_TYPES = {
  // Document-centric — cliente sobe ou pede produção de documento
  CONTRACT_REVIEW_SIMPLE: 'contract_review_simple',
  CONTRACT_REVIEW_COMPLEX: 'contract_review_complex',
  CONTRACT_DRAFTING: 'contract_drafting',
  WRITTEN_OPINION: 'written_opinion',
  // Service / consultation — pode não ter documento
  CHAT_CONSULTATION: 'chat_consultation',
  CALL_CONSULTATION: 'call_consultation',
  DIAGNOSTIC_MEETING: 'diagnostic_meeting',
  PREVENTIVE_ADVICE: 'preventive_advice',
  STRATEGIC_CONSULTING: 'strategic_consulting',
  // Casos com escopo nominal — situação específica do cliente
  CASE_ANALYSIS: 'case_analysis',
  FAMILY_LAW_SERVICE: 'family_law_service',
  OUT_OF_COURT_NEGOTIATION: 'out_of_court_negotiation',
} as const;

export type TaskType = (typeof TASK_TYPES)[keyof typeof TASK_TYPES];

export const TASK_TYPE_LABELS: Record<TaskType, string> = {
  contract_review_simple: 'Revisão de contrato simples',
  contract_review_complex: 'Revisão de contrato complexo',
  contract_drafting: 'Redação de contrato do zero',
  written_opinion: 'Parecer jurídico escrito',
  chat_consultation: 'Chat consultivo',
  call_consultation: 'Consulta por call',
  diagnostic_meeting: 'Reunião de diagnóstico',
  preventive_advice: 'Orientação preventiva',
  strategic_consulting: 'Consultoria estratégica',
  case_analysis: 'Análise de caso',
  family_law_service: 'Serviço de direito de família',
  out_of_court_negotiation: 'Negociação extrajudicial',
};

/**
 * Per-task-type hint shown in the UI + fed into the AI triage prompt so the
 * model knows what each label means. `needsDocument` says whether the cliente
 * tipicamente precisa anexar um documento — orienta o fluxo de criação.
 */
export const TASK_TYPE_HINTS: Record<TaskType, { description: string; needsDocument: 'required' | 'optional' | 'none' }> = {
  contract_review_simple: {
    description: 'Cliente já tem um contrato (até ~10 páginas, baixa complexidade) e precisa de revisão jurídica.',
    needsDocument: 'required',
  },
  contract_review_complex: {
    description: 'Contrato extenso, com cláusulas atípicas ou alto valor envolvido, exigindo análise aprofundada.',
    needsDocument: 'required',
  },
  contract_drafting: {
    description: 'Redação de um contrato do zero a partir de descrição do escopo desejado.',
    needsDocument: 'optional',
  },
  written_opinion: {
    description: 'Parecer jurídico escrito sobre uma questão específica — fundamentação detalhada com base em legislação e jurisprudência.',
    needsDocument: 'optional',
  },
  chat_consultation: {
    description: 'Chat assíncrono para tirar dúvidas pontuais. Ideal para perguntas rápidas sem necessidade de reunião.',
    needsDocument: 'none',
  },
  call_consultation: {
    description: 'Videocall de até 60 minutos para discutir uma situação específica. Sem material prévio necessário.',
    needsDocument: 'optional',
  },
  diagnostic_meeting: {
    description: 'Reunião mais longa para mapear uma situação complexa e desenhar caminhos jurídicos possíveis. Pode incluir análise prévia de documentos.',
    needsDocument: 'optional',
  },
  preventive_advice: {
    description: 'Orientação preventiva sobre algo que o cliente pretende fazer ("vou comprar uma empresa", "vou contratar um sócio"). Foco em evitar problemas futuros.',
    needsDocument: 'none',
  },
  strategic_consulting: {
    description: 'Consultoria estratégica de médio/longo prazo, acompanhando o cliente em decisões recorrentes (ex.: governança de startup, conformidade regulatória).',
    needsDocument: 'none',
  },
  case_analysis: {
    description: 'Análise de um caso já em andamento (processo em curso, situação pré-litigiosa) para mapear riscos, posições e caminhos.',
    needsDocument: 'optional',
  },
  family_law_service: {
    description: 'Atendimento em Direito de Família: divórcio (consensual ou litigioso), guarda, pensão alimentícia, inventário, conversões de união. Documento inicial frequentemente ausente.',
    needsDocument: 'none',
  },
  out_of_court_negotiation: {
    description: 'Mediação ou negociação extrajudicial — acordo entre as partes sem ir a juízo. Pode envolver minuta de termo de acordo ao fim.',
    needsDocument: 'optional',
  },
};

export const LANGUAGES = ['pt', 'en', 'es', 'fr', 'de', 'it'] as const;
export type Language = (typeof LANGUAGES)[number];

export const LANGUAGE_LABELS: Record<Language, string> = {
  pt: 'Português',
  en: 'Inglês',
  es: 'Espanhol',
  fr: 'Francês',
  de: 'Alemão',
  it: 'Italiano',
};

export const URGENCY_LEVELS = ['low', 'medium', 'high'] as const;
export type Urgency = (typeof URGENCY_LEVELS)[number];

export const URGENCY_LABELS: Record<Urgency, string> = {
  low: 'Sem pressa — preventivo ou planejamento',
  medium: 'Algumas semanas — preciso resolver em breve',
  high: 'Urgente — questão de dias',
};

export const TASK_STATUS = {
  OPEN: 'open',
  ASSIGNED: 'assigned',
  // Lawyer formally proposed scope + deadline + price; cliente has not yet
  // accepted/rejected. While in this state, the cliente sees the proposal
  // card on the task page. Accept → checkout → IN_PROGRESS; reject → ASSIGNED.
  PROPOSED: 'proposed',
  IN_PROGRESS: 'in_progress',
  DELIVERED: 'delivered',
  COMPLETED: 'completed',
  REJECTED: 'rejected',
  CANCELLED: 'cancelled',
  DISPUTED: 'disputed',
} as const;

export type TaskStatus = (typeof TASK_STATUS)[keyof typeof TASK_STATUS];

/**
 * ─── Taxonomia de especialidades em 3 camadas ─────────────────────────────
 *
 *   PRACTICE_AREAS  (15 grandes áreas)
 *       └── SPECIALTIES  (74 especialidades, agrupadas por área via SPECIALTY_AREA)
 *               └── tags livres no perfil do advogado (free-form, max 20)
 *
 * Por que assim e não plano:
 *   - "M&A" é uma operação dentro de Societário, não um irmão dele.
 *   - "Incoterms" é um tema dentro de Contratos Internacionais.
 *   - Família/Previdenciário/Penal/Médico tinham demanda popular alta e
 *     estavam de fora.
 *   - Tags livres absorvem nichos emergentes (Web3, IA, SAFE, NDA, vesting,
 *     Lei do Bem, MEI, Simples Nacional…) sem inflar o enum.
 *
 * Migração de chaves antigas: ver `LEGACY_SPECIALTY_MAP` abaixo. O script
 * `apps/api/scripts/backfill-specialties.ts` aplica esse mapping nos
 * perfis em produção.
 */
export const PRACTICE_AREAS = [
  'empresarial',
  'trabalho',
  'tributario_fiscal',
  'familia_sucessoes',
  'imobiliario_urbanistico',
  'civil_consumidor',
  'penal',
  'digital_tech',
  'propriedade_intelectual',
  'internacional',
  'medico_saude',
  'publico',
  'esg_sustentabilidade',
  'resolucao_conflitos',
  'nichos',
] as const;

export type PracticeArea = (typeof PRACTICE_AREAS)[number];

export const PRACTICE_AREA_LABELS: Record<PracticeArea, string> = {
  empresarial: 'Direito Empresarial & Negócios',
  trabalho: 'Direito do Trabalho',
  tributario_fiscal: 'Direito Tributário & Fiscal',
  familia_sucessoes: 'Direito de Família & Sucessões',
  imobiliario_urbanistico: 'Direito Imobiliário & Urbanístico',
  civil_consumidor: 'Direito Civil & Consumidor',
  penal: 'Direito Penal',
  digital_tech: 'Direito Digital & Tecnologia',
  propriedade_intelectual: 'Propriedade Intelectual',
  internacional: 'Direito Internacional & Comércio Exterior',
  medico_saude: 'Direito Médico & da Saúde',
  publico: 'Direito Público',
  esg_sustentabilidade: 'ESG, Sustentabilidade & Terceiro Setor',
  resolucao_conflitos: 'Resolução de Conflitos',
  nichos: 'Nichos Especializados',
};

export const SPECIALTIES = [
  // 1. Empresarial & Negócios
  'contratos_comerciais',
  'societario',
  'ma',
  'recuperacao_falencia',
  'startups_vc_pe',
  'bancario_financeiro',
  'securitario',
  'concorrencial',
  'compliance_anticorrupcao',
  'governanca_corporativa',
  // 2. Trabalho
  'trabalhista_empregador',
  'trabalhista_empregado',
  'previdenciario',
  'pejotizacao_pj',
  'saude_seguranca_trabalho',
  // 3. Tributário & Fiscal
  'tributario_empresarial',
  'tributario_pf',
  'aduaneiro',
  'simples_mei',
  // 4. Família & Sucessões
  'familia',
  'sucessoes_inventario',
  'planejamento_sucessorio',
  'testamentos',
  'adocao_filiacao',
  // 5. Imobiliário & Urbanístico
  'imobiliario',
  'locacao',
  'compra_venda_imoveis',
  'incorporacao_loteamento',
  'condominios',
  'regularizacao_usucapiao',
  // 6. Civil & Consumidor
  'civil',
  'consumidor',
  'responsabilidade_civil',
  'cobranca_credito',
  // 7. Penal
  'penal_empresarial',
  'penal_geral',
  'compliance_criminal',
  // 8. Digital & Tecnologia
  'direito_digital',
  'lgpd',
  'marco_civil',
  'cibercrimes',
  'contratos_tecnologia',
  'web3_cripto',
  'ia_regulacao',
  // 9. Propriedade Intelectual
  'propriedade_intelectual',
  'marcas_patentes',
  'direito_autoral',
  'software_open_source',
  'entretenimento_midia',
  // 10. Internacional & Comércio Exterior
  'internacional_privado',
  'contratos_internacionais',
  'arbitragem_internacional',
  'imigracao_vistos',
  'expatriados_tributacao',
  // 11. Médico & Saúde
  'medico_hospitalar',
  'erro_medico',
  'planos_saude',
  'bioetica_anvisa',
  // 12. Público
  'administrativo',
  'constitucional',
  'eleitoral',
  'regulatorio_setorial',
  // 13. ESG, Sustentabilidade & Terceiro Setor
  'esg',
  'ambiental',
  'terceiro_setor',
  'direito_animal',
  // 14. Resolução de Conflitos
  'arbitragem_adr',
  'mediacao_conciliacao',
  'contencioso_civel',
  // 15. Nichos Especializados
  'desportivo',
  'educacional',
  'maritimo_portuario',
  'aeronautico',
  'influencers_marketing',
] as const;

export type Specialty = (typeof SPECIALTIES)[number];

export const SPECIALTY_LABELS: Record<Specialty, string> = {
  // 1. Empresarial
  contratos_comerciais: 'Contratos comerciais',
  societario: 'Societário',
  ma: 'M&A — Fusões e Aquisições',
  recuperacao_falencia: 'Recuperação Judicial e Falência',
  startups_vc_pe: 'Startups, VC e Private Equity',
  bancario_financeiro: 'Bancário e Mercado Financeiro',
  securitario: 'Securitário (seguros, resseguros)',
  concorrencial: 'Concorrencial / CADE',
  compliance_anticorrupcao: 'Compliance e Anticorrupção (Lei 12.846)',
  governanca_corporativa: 'Governança Corporativa',
  // 2. Trabalho
  trabalhista_empregador: 'Trabalhista — Empregador (PJ)',
  trabalhista_empregado: 'Trabalhista — Empregado/Sindical',
  previdenciario: 'Previdenciário (INSS, aposentadoria, BPC)',
  pejotizacao_pj: 'Pejotização e Trabalho PJ/MEI',
  saude_seguranca_trabalho: 'Saúde e Segurança do Trabalho',
  // 3. Tributário
  tributario_empresarial: 'Tributário Empresarial',
  tributario_pf: 'Tributário PF (IR, doações, herança)',
  aduaneiro: 'Aduaneiro e Comércio Exterior',
  simples_mei: 'Simples Nacional e MEI',
  // 4. Família
  familia: 'Família (divórcio, guarda, pensão, união estável)',
  sucessoes_inventario: 'Sucessões e Inventário',
  planejamento_sucessorio: 'Planejamento Sucessório e Holding Familiar',
  testamentos: 'Testamentos',
  adocao_filiacao: 'Adoção e Filiação',
  // 5. Imobiliário
  imobiliario: 'Imobiliário',
  locacao: 'Locação Residencial e Comercial',
  compra_venda_imoveis: 'Compra, Venda e Permuta de Imóveis',
  incorporacao_loteamento: 'Incorporação e Loteamento',
  condominios: 'Condomínios',
  regularizacao_usucapiao: 'Regularização Fundiária e Usucapião',
  // 6. Civil & Consumidor
  civil: 'Civil',
  consumidor: 'Direito do Consumidor',
  responsabilidade_civil: 'Responsabilidade Civil e Danos Morais',
  cobranca_credito: 'Cobrança e Recuperação de Crédito',
  // 7. Penal
  penal_empresarial: 'Penal Empresarial',
  penal_geral: 'Penal Geral (defesa criminal)',
  compliance_criminal: 'Compliance Criminal e Investigações Internas',
  // 8. Digital
  direito_digital: 'Direito Digital',
  lgpd: 'LGPD e Proteção de Dados',
  marco_civil: 'Marco Civil da Internet e Conteúdo Online',
  cibercrimes: 'Cibercrimes',
  contratos_tecnologia: 'Contratos de Tecnologia (SaaS, licenciamento, cloud)',
  web3_cripto: 'Web3, Cripto e Blockchain',
  ia_regulacao: 'Inteligência Artificial e Marco da IA (PL 2338)',
  // 9. PI
  propriedade_intelectual: 'Propriedade Intelectual',
  marcas_patentes: 'Marcas e Patentes (INPI)',
  direito_autoral: 'Direito Autoral',
  software_open_source: 'Software e Open Source',
  entretenimento_midia: 'Entretenimento, Música e Audiovisual',
  // 10. Internacional
  internacional_privado: 'Internacional Privado',
  contratos_internacionais: 'Contratos Internacionais e Incoterms',
  arbitragem_internacional: 'Arbitragem Internacional',
  imigracao_vistos: 'Imigração e Vistos',
  expatriados_tributacao: 'Expatriados e Tributação Internacional',
  // 11. Médico
  medico_hospitalar: 'Médico e Hospitalar',
  erro_medico: 'Erro Médico e Responsabilidade',
  planos_saude: 'Planos de Saúde',
  bioetica_anvisa: 'Bioética e Regulatório ANVISA',
  // 12. Público
  administrativo: 'Administrativo (licitações, PPPs, concessões)',
  constitucional: 'Constitucional',
  eleitoral: 'Eleitoral',
  regulatorio_setorial: 'Regulatório Setorial',
  // 13. ESG
  esg: 'ESG e Compliance Ambiental',
  ambiental: 'Ambiental',
  terceiro_setor: 'Terceiro Setor / OSCs / Institutos',
  direito_animal: 'Direito Animal',
  // 14. Resolução
  arbitragem_adr: 'Arbitragem e ADR (genérico)',
  mediacao_conciliacao: 'Mediação e Conciliação',
  contencioso_civel: 'Contencioso Cível Estratégico',
  // 15. Nichos
  desportivo: 'Desportivo',
  educacional: 'Educacional',
  maritimo_portuario: 'Marítimo e Portuário',
  aeronautico: 'Aeronáutico',
  influencers_marketing: 'Influencers, Criadores e Marketing Digital',
};

/** Maps each specialty to its parent practice area. */
export const SPECIALTY_AREA: Record<Specialty, PracticeArea> = {
  contratos_comerciais: 'empresarial',
  societario: 'empresarial',
  ma: 'empresarial',
  recuperacao_falencia: 'empresarial',
  startups_vc_pe: 'empresarial',
  bancario_financeiro: 'empresarial',
  securitario: 'empresarial',
  concorrencial: 'empresarial',
  compliance_anticorrupcao: 'empresarial',
  governanca_corporativa: 'empresarial',
  trabalhista_empregador: 'trabalho',
  trabalhista_empregado: 'trabalho',
  previdenciario: 'trabalho',
  pejotizacao_pj: 'trabalho',
  saude_seguranca_trabalho: 'trabalho',
  tributario_empresarial: 'tributario_fiscal',
  tributario_pf: 'tributario_fiscal',
  aduaneiro: 'tributario_fiscal',
  simples_mei: 'tributario_fiscal',
  familia: 'familia_sucessoes',
  sucessoes_inventario: 'familia_sucessoes',
  planejamento_sucessorio: 'familia_sucessoes',
  testamentos: 'familia_sucessoes',
  adocao_filiacao: 'familia_sucessoes',
  imobiliario: 'imobiliario_urbanistico',
  locacao: 'imobiliario_urbanistico',
  compra_venda_imoveis: 'imobiliario_urbanistico',
  incorporacao_loteamento: 'imobiliario_urbanistico',
  condominios: 'imobiliario_urbanistico',
  regularizacao_usucapiao: 'imobiliario_urbanistico',
  civil: 'civil_consumidor',
  consumidor: 'civil_consumidor',
  responsabilidade_civil: 'civil_consumidor',
  cobranca_credito: 'civil_consumidor',
  penal_empresarial: 'penal',
  penal_geral: 'penal',
  compliance_criminal: 'penal',
  direito_digital: 'digital_tech',
  lgpd: 'digital_tech',
  marco_civil: 'digital_tech',
  cibercrimes: 'digital_tech',
  contratos_tecnologia: 'digital_tech',
  web3_cripto: 'digital_tech',
  ia_regulacao: 'digital_tech',
  propriedade_intelectual: 'propriedade_intelectual',
  marcas_patentes: 'propriedade_intelectual',
  direito_autoral: 'propriedade_intelectual',
  software_open_source: 'propriedade_intelectual',
  entretenimento_midia: 'propriedade_intelectual',
  internacional_privado: 'internacional',
  contratos_internacionais: 'internacional',
  arbitragem_internacional: 'internacional',
  imigracao_vistos: 'internacional',
  expatriados_tributacao: 'internacional',
  medico_hospitalar: 'medico_saude',
  erro_medico: 'medico_saude',
  planos_saude: 'medico_saude',
  bioetica_anvisa: 'medico_saude',
  administrativo: 'publico',
  constitucional: 'publico',
  eleitoral: 'publico',
  regulatorio_setorial: 'publico',
  esg: 'esg_sustentabilidade',
  ambiental: 'esg_sustentabilidade',
  terceiro_setor: 'esg_sustentabilidade',
  direito_animal: 'esg_sustentabilidade',
  arbitragem_adr: 'resolucao_conflitos',
  mediacao_conciliacao: 'resolucao_conflitos',
  contencioso_civel: 'resolucao_conflitos',
  desportivo: 'nichos',
  educacional: 'nichos',
  maritimo_portuario: 'nichos',
  aeronautico: 'nichos',
  influencers_marketing: 'nichos',
};

/**
 * Legacy → new mapping. Run via `apps/api/scripts/backfill-specialties.ts`
 * to upgrade existing lawyer profiles. Targets are the closest semantic
 * match; lawyer can refine after backfill.
 *
 * The 10 identity keys (contratos_comerciais, societario, ma, propriedade_
 * intelectual, civil, consumidor, imobiliario, administrativo, internacional
 * _privado, arbitragem_adr) are omitted — backfill skips them automatically.
 */
export const LEGACY_SPECIALTY_MAP: Record<string, Specialty> = {
  trabalhista: 'trabalhista_empregador',
  startup_vc: 'startups_vc_pe',
  digital_lgpd: 'lgpd',
  esg_compliance: 'esg',
  tributario: 'tributario_empresarial',
};

/** Helper: returns the specialties belonging to a given practice area. */
export function specialtiesInArea(area: PracticeArea): Specialty[] {
  return SPECIALTIES.filter((s) => SPECIALTY_AREA[s] === area);
}

export const RISK_LEVELS = ['low', 'medium', 'high'] as const;
export type RiskLevel = (typeof RISK_LEVELS)[number];

export const SENIORITY_LEVELS = ['junior', 'pleno', 'senior', 'master'] as const;
export type Seniority = (typeof SENIORITY_LEVELS)[number];

export const SENIORITY_LABELS: Record<Seniority, string> = {
  junior: 'Junior',
  pleno: 'Pleno',
  senior: 'Sênior',
  master: 'Master',
};

/**
 * Hint shown next to the seniority picker in the lawyer profile editor.
 * Subjective by design — gives the lawyer a frame of reference but doesn't
 * gate anything. Admin verification (OAB + certifications) is what carries
 * actual weight on the platform.
 */
export const SENIORITY_DESCRIPTIONS: Record<Seniority, string> = {
  junior: 'Até 3 anos de exercício pós-OAB',
  pleno: '3 a 7 anos de exercício',
  senior: '7 a 15 anos de exercício, com especialização ou pós-graduação',
  master: '15+ anos, com LL.M./Mestrado/Doutorado ou liderança em escritório/área',
};

export const SIGNATURE_STATUS = {
  NONE: 'none',                 // não enviado pra assinatura
  PENDING: 'pending',           // enviado, aguardando todos assinarem
  PARTIALLY_SIGNED: 'partially_signed', // alguns signatários assinaram
  SIGNED: 'signed',             // todos assinaram
  REJECTED: 'rejected',         // algum signatário recusou
  EXPIRED: 'expired',           // prazo de assinatura expirou
  CANCELLED: 'cancelled',       // emissor cancelou
} as const;

export type SignatureStatus = (typeof SIGNATURE_STATUS)[keyof typeof SIGNATURE_STATUS];

export const SIGNATURE_PROVIDERS = ['clicksign', 'd4sign', 'autentique'] as const;
export type SignatureProvider = (typeof SIGNATURE_PROVIDERS)[number];

export const PAYMENT_STATUS = {
  PENDING: 'pending',
  CONFIRMED: 'confirmed',
  ESCROWED: 'escrowed',
  RELEASED: 'released',
  REFUNDED: 'refunded',
  FAILED: 'failed',
} as const;

export type PaymentStatus = (typeof PAYMENT_STATUS)[keyof typeof PAYMENT_STATUS];

/** Default platform fee. Override via env var in services that need it. */
export const DEFAULT_PLATFORM_FEE_PERCENT = 15;

export const FILE_LIMITS = {
  MAX_CONTRACT_SIZE_BYTES: 20 * 1024 * 1024,
  MAX_ATTACHMENT_SIZE_BYTES: 10 * 1024 * 1024,
  ALLOWED_CONTRACT_MIME_TYPES: [
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  ] as const,
  ALLOWED_ATTACHMENT_MIME_TYPES: [
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'image/png',
    'image/jpeg',
  ] as const,
} as const;

export const PDF_MAGIC_BYTES = new Uint8Array([0x25, 0x50, 0x44, 0x46]);
export const DOCX_MAGIC_BYTES = new Uint8Array([0x50, 0x4b, 0x03, 0x04]);
